import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { DevForm } from "@components/Forms/Fields";
import { Hint } from "@components/Helpers";

const FIELD_STATUS_LABELS = {
    fixed: "🔵", // Исправлено
    not_fixed: "🔴", // Не исправлено
    changed_by_developer: "🟡", // Изменено по инициативе разработчика
};

function ModerationFieldItem({ field, onChange, inReview = false }) {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [comment, setComment] = useState(() => {
        if (field.status === "not_fixed" && field.comments_history?.length > 0) {
            return field.comments_history[0];
        }
        return field.comment || "";
    });

    // Синхронизируем инициализированное значение с родительской формой
    useEffect(() => {
        if (comment && comment !== field.comment && onChange) {
            onChange(field.name, comment);
        }
    }, []);

    const statusLabel = FIELD_STATUS_LABELS[field.status];

    const renderSysReq = (data) => {
        const sections = [
            { key: "min", label: t("Минимальные") },
            { key: "rec", label: t("Рекомендуемые") },
        ];
        return (
            <div>
                {sections.map(({ key, label }) => {
                    const req = data[key];
                    if (!req) return null;
                    return (
                        <div key={key} className="mb-3">
                            <strong>{label}:</strong>
                            <table className="table table-sm table-bordered mt-1 mb-0">
                                <tbody>
                                    {req.os && (
                                        <tr>
                                            <td className="text-muted" style={{ width: "140px" }}>
                                                ОС
                                            </td>
                                            <td>{Array.isArray(req.os) ? req.os.join(", ") : req.os}</td>
                                        </tr>
                                    )}
                                    {req.cpu && (
                                        <tr>
                                            <td className="text-muted">Процессор</td>
                                            <td>{req.cpu}</td>
                                        </tr>
                                    )}
                                    {req.ram && (
                                        <tr>
                                            <td className="text-muted">RAM</td>
                                            <td>{req.ram}</td>
                                        </tr>
                                    )}
                                    {req.hdd && (
                                        <tr>
                                            <td className="text-muted">HDD</td>
                                            <td>{req.hdd}</td>
                                        </tr>
                                    )}
                                    {req.videocard && (
                                        <tr>
                                            <td className="text-muted">Видеокарта</td>
                                            <td>{req.videocard}</td>
                                        </tr>
                                    )}
                                    {req.other && (
                                        <tr>
                                            <td className="text-muted">Другое</td>
                                            <td>{req.other}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderValue = (value, name, metaData) => {
        if (value === null || value === undefined || value === "") {
            return <span className="text-muted">{t("Не заполнено")}</span>;
        }
        if (name.startsWith("about_text") && typeof value === "string") {
            return <div dangerouslySetInnerHTML={{ __html: value }} />;
        }
        if (name === "video") {
            return (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    {value}
                </a>
            );
        }
        if (typeof value === "string" && value.startsWith("/hotbox/")) {
            return (
                <a href={value} target="_blank" rel="noopener noreferrer">
                    <img src={value} alt="" style={{ maxWidth: "200px", maxHeight: "200px", objectFit: "contain" }} />
                </a>
            );
        }
        if (name && name.startsWith("highlights") && Array.isArray(value)) {
            if (value.length === 0 || (typeof value[0] === "object" && value[0].type)) {
                return (
                    <div>
                        {value.map((item, index) => (
                            <div key={index} className="mb-2">
                                {item.type === "image" && (
                                    <div>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                                            <img
                                                src={item.url}
                                                alt=""
                                                style={{
                                                    maxWidth: "200px",
                                                    maxHeight: "200px",
                                                    objectFit: "contain",
                                                    display: "block",
                                                }}
                                            />
                                        </a>
                                    </div>
                                )}
                                {item.type === "video_vk" && (
                                    <div>
                                        <Badge variant="primary" className="mr-2">
                                            {t("Видео VK")}
                                        </Badge>
                                        {"ID"}: {item.vkvideo_id}
                                    </div>
                                )}
                                {item.type === "video_url" && (
                                    <div>
                                        <Badge variant="secondary" className="mr-2">
                                            {t("Видео по ссылке")}
                                        </Badge>
                                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                                            {item.url}
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                );
            }
            return value.join(", ");
        }

        if (name === "countries" && Array.isArray(value)) {
            if (metaData?.presets?.length > 0) {
                const presetNames = {
                    russia: t("Страны и территории с русской локализацией"),
                    ww: t("Весь мир"),
                    asia: t("Азия"),
                    africa: t("Африка"),
                    oceania: t("Океания"),
                    europe: t("Европа"),
                    america: t("Америка"),
                };
                return (
                    <div>
                        {metaData.presets.map((preset) => (
                            <Button key={preset} size="sm" variant="info" className="mr-1 mb-1" disabled>
                                {presetNames[preset] || preset}
                            </Button>
                        ))}
                        <div className="mt-2">{value.join(", ")}</div>
                    </div>
                );
            }
            return value.join(", ");
        }

        if (Array.isArray(value)) {
            return value.join(", ");
        }
        if (name.startsWith("social_links") && typeof value === "object") {
            return (
                <table className="table table-sm table-bordered mt-1 mb-0">
                    <tbody>
                        {Object.entries(value).map(([key, val]) => (
                            <tr key={key}>
                                <td className="text-muted" style={{ width: "140px" }}>
                                    {key}
                                </td>
                                <td>{String(val)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            );
        }
        if (name.startsWith("sys_req_web_data") && typeof value === "object") {
            return renderSysReq(value);
        }
        if (typeof value === "object") {
            return JSON.stringify(value);
        }
        return String(value);
    };

    const handleChange = (fieldName, value) => {
        setComment(value);
        if (onChange) {
            onChange(fieldName, value);
        }
    };

    return (
        <Card className="mb-2">
            <Card.Header
                className="d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => setExpanded(!expanded)}
            >
                <div className="d-flex align-items-center">
                    {field.status && <Badge>{statusLabel}</Badge>}
                    {field.status && (
                        <Hint
                            className="mr-2"
                            tooltip={
                                field.status === "fixed"
                                    ? t("Исправлено")
                                    : field.status === "not_fixed"
                                      ? t("Не исправлено")
                                      : t("Изменено по инициативе разработчика")
                            }
                        />
                    )}
                    <strong>
                        {field.label}
                        {field.locale && (
                            <span className="text-muted ml-1" style={{ fontSize: "0.85em" }}>
                                ({field.locale})
                            </span>
                        )}
                    </strong>
                </div>
                <span>{expanded ? "▼" : "▶"}</span>
            </Card.Header>

            {expanded && (
                <Card.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <strong>{t("Значение поля")}:</strong>
                            <div className="mt-1 p-2 bg-light border rounded">
                                {renderValue(field.value, field.name, field.meta_data)}
                            </div>
                        </div>

                        {inReview && (
                            <div className="mb-3">
                                <strong>{t("Предыдущее значение")}:</strong>
                                <div className="mt-1 p-2 bg-light border rounded">
                                    {renderValue(field.previous_value, field.name, field.meta_data)}
                                </div>
                            </div>
                        )}
                    </div>

                    <Form.Group>
                        <Form.Label>{t("Комментарии разработчику")}</Form.Label>
                        <DevForm.TextArea
                            name={`field-comment-${field.name}`}
                            value={comment}
                            onChange={(ev) => handleChange(field.name, ev.target.value)}
                            rows={3}
                        />
                    </Form.Group>

                    {field.comments_history && field.comments_history.length > 0 && (
                        <Card className="mt-3">
                            <Card.Header
                                className="d-flex justify-content-between align-items-center"
                                style={{ cursor: "pointer", padding: "0.5rem 1rem" }}
                                onClick={() => setHistoryOpen(!historyOpen)}
                            >
                                <strong style={{ fontSize: "0.9rem" }}>
                                    {t("Предыдущие комментарии разработчику")} ({field.comments_history.length})
                                </strong>
                                <span>{historyOpen ? "▼" : "▶"}</span>
                            </Card.Header>
                            {historyOpen && (
                                <Card.Body style={{ padding: "0.5rem 1rem" }}>
                                    <div
                                        className="p-2 bg-light border rounded"
                                        style={{ maxHeight: "200px", overflowY: "auto", whiteSpace: "pre-wrap" }}
                                    >
                                        {field.comments_history.map((c, idx) => (
                                            <div key={idx} className={idx > 0 ? "mt-2 pt-2 border-top" : ""}>
                                                {c}
                                            </div>
                                        ))}
                                    </div>
                                </Card.Body>
                            )}
                        </Card>
                    )}
                </Card.Body>
            )}
        </Card>
    );
}

export function ModerationFieldsList({ fields, onFieldCommentChange, inReview = false }) {
    const { t } = useTranslation();

    if (!fields || fields.length === 0) {
        return null;
    }

    const handleChange = (fieldName, comment) => {
        if (onFieldCommentChange) {
            onFieldCommentChange(fieldName, comment);
        }
    };

    return (
        <div className="moderation-fields-list">
            <h5 className="mb-3">{t("Поля для проверки")}</h5>
            {fields.map((field, index) => (
                <ModerationFieldItem
                    key={`field-${field.name}-${index}`}
                    field={field}
                    onChange={handleChange}
                    inReview={inReview}
                />
            ))}
        </div>
    );
}
